/**
 * Responsibility: read this person's own data straight from Firestore.
 *
 * **This is what makes the site survive the hub.** Everything a person typed -
 * their collection, likes, saved searches, topics, settings - lives in
 * Firestore, and until now the browser reached it only through the Oracle box.
 * That meant losing the box lost the site as well as the analysis, which is the
 * one thing moving the data to Firestore was supposed to prevent.
 *
 * The hub is still the only way to *change* most of it. Adding a card looks the
 * product up in the catalogue, a notification channel writes a credential the
 * browser may not see, and both of those need a server. So: reads here, writes
 * there, and a banner when there is no there.
 *
 * **Offline persistence is on.** Firestore keeps a local copy in IndexedDB and
 * serves it when the network is gone, which covers the aeroplane case as well
 * as the Oracle case. `persistentLocalCache` with a single-tab manager because
 * multi-tab synchronisation needs a leader election that silently degrades on
 * browsers without it; one tab is what a phone has anyway.
 */

import { getApp, getApps } from 'firebase/app';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  type Firestore,
} from 'firebase/firestore';

import { firebaseConfigured } from '../auth/firebase';

let cached: Firestore | null = null;

/**
 * The Firestore instance, or null in a build with no project.
 *
 * Null rather than throwing, for the same reason `auth` is: a build without
 * configuration is a real state - a fresh checkout, and CI - and it should say
 * so on screen rather than fail somewhere inside the SDK.
 */
export function db(): Firestore | null {
  if (!firebaseConfigured || getApps().length === 0) return null;
  if (cached) return cached;

  const app = getApp();
  try {
    cached = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) }),
    });
  } catch {
    // Already initialised - a hot reload, or something else got there first.
    // The instance is per app, so asking for it plainly is correct.
    cached = getFirestore(app);
  }
  return cached;
}
